import { NumberUtils, OrderConfigResponse, PaymentUtils } from '@ca/common'
import { useOrderState } from '@order/hooks/useOrderState'
import { useSavedOrderSummary } from '@order/hooks/useOrderSummary'
import { Discounts } from '@order/utils/discounts/Discounts'
import useSession, { resolveDealsContext } from 'hooks/useSession'
import { useMemo } from 'react'
import { ResolvedDealsContext, SavedOrder } from './state/orderState'
import useDeal, { getLocalizedDealById, useDealsById } from './useDeal'
import { useCustomDealSettings } from './useDealSettings'
import { getDealSettings, shouldShowMonthly } from '@ca/common/types'
import { useTranslation } from 'react-i18next'
import { useSavedOrder } from './useCartState'
import AppConfig from 'config/AppConfig'
import { useCurrencyDecimals } from 'hooks/useCurrency'

export const getInstallmentValue = (
  value: number,
  installmentCount?: number
) => {
  if (!installmentCount) {
    return value
  }
  if (installmentCount === 0) {
    return value
  }
  const amount = value / installmentCount
  return NumberUtils.ceil.decimal(amount)
}

const calculateTotal = (
  result?: OrderConfigResponse | null,
  installments?: number
) => {
  const total = getResultTotal(result)
  if (total === undefined) {
    return undefined
  }
  return getInstallmentValue(total, installments)
}

export const getOrderResolvedAmount = (order: {
  amount: number
  final_price?: number
}) => {
  return order.final_price ?? order.amount
}

export const getResultTotal = (result?: OrderConfigResponse | null) => {
  if (!result) {
    return undefined
  }
  if (result.orders.length === 0) {
    return result.total
  }
  const prices = result.orders.map((order) => getOrderResolvedAmount(order))
  return NumberUtils.round.decimal(
    prices.reduce((acc, price) => acc + price, 0)
  )
}

export const hasResultFinalPrices = (result?: OrderConfigResponse | null) => {
  return !!result?.orders.some((order) => order.final_price !== undefined)
}

/**
 * Prices don't include user applied discounts ie (codes, credits)
 */
export type OrderTotal = {
  /**
   * Value from the ERP
   */
  remote: number | undefined
  /**
   * Value calculated from the local summary state
   */
  local: number
  /**
   * Value adjusted to number of installments, if 0 its same as remote
   * Prefer this when displaying with prefix/suffix
   */
  display: number
  /**
   * Resolved total value, if remote is defined, it will be remote, otherwise local
   */
  resolved: number
  /**
   * Total discount amount
   */
  installmentCount: number
  /**
   * True when at least one ERP order has final_price set.
   */
  hasFinalPrices: boolean
  /**
   * True when `resolved` already has the user applied discounts subtracted,
   * so consumers must not subtract them a second time.
   */
  includesDiscounts: boolean
  /**
   * Controls whether UI should display monthly values/suffixes.
   * Payments can still use installment math independently.
   */
  showMonthly: boolean
}

/**
 * Prices don't include user applied discounts ie (codes, credits)
 * @returns OrderTotal
 */
export const useOrderResultTotal = ({
  result,
  order,
  deals,
}: Pick<SavedOrder, 'result' | 'order' | 'deals'>) => {
  const summary = useSavedOrderSummary(order, deals)
  const deal = useDeal(deals.base?.dealId)
  const settings = useCustomDealSettings(deal)
  return useMemo(() => {
    const remote = calculateTotal(result)
    const local = summary ? Discounts.unwrap(summary.price) : 0
    const installments = settings?.installments ?? 1
    const display = calculateTotal(result, settings?.installments)
    const hasFinalPrices = hasResultFinalPrices(result)
    const installmentCount = settings?.installments ?? 0
    const showMonthly = shouldShowMonthly(settings)

    return {
      remote,
      local,
      display: display ?? local,
      resolved: remote ?? local * installments,
      installmentCount,
      hasFinalPrices,
      includesDiscounts: false,
      showMonthly,
    } as OrderTotal
  }, [result, summary, settings])
}

/**
 * This skips local calculation and only uses the remote total
 * Use only if result is already preset
 * Prices don't include user applied discounts ie (codes, credits)
 * @returns OrderTotal
 */
export const useRemoteOnlyTotal = ({
  deals,
  result,
}: {
  deals: ResolvedDealsContext
  result: OrderConfigResponse | null
}) => {
  const deal = useDeal(deals.base?.dealId)

  const settings = useCustomDealSettings(deal)

  return useMemo(() => {
    const remote = calculateTotal(result)
    const display = calculateTotal(result, settings?.installments)
    const local = remote ?? 0
    const hasFinalPrices = hasResultFinalPrices(result)
    const installmentCount = settings?.installments ?? 0
    const showMonthly = shouldShowMonthly(settings)
    return {
      remote,
      local,
      display: display ?? local,
      resolved: remote ?? local,
      installmentCount,
      hasFinalPrices,
      includesDiscounts: false,
      showMonthly,
    } satisfies OrderTotal
  }, [result, settings])
}

/**
 * Unlike the other totals, prices here DO include user applied discounts:
 * per-order final_price when present, else amount reduced by discounts.
 * Flagged with `includesDiscounts` so consumers don't subtract them twice.
 * @returns OrderTotal
 */
export const useMultiOrderTotal = (orders: SavedOrder[]): OrderTotal => {
  const decimals = useCurrencyDecimals()
  const totals = {
    remote: 0,
    local: 0,
    installments: 0,
    installmentCount: 0,
    hasFinalPrices: false,
  }

  const { i18n } = useTranslation()
  const deals = useDealsById()
  const dealLang = AppConfig.dealLang || i18n.language

  const allOrders = orders.flatMap((o) => o.result?.orders ?? [])
  const allDiscounts = orders.flatMap((o) => o.discounts)
  const resolvedTotal =
    allOrders.length > 0
      ? PaymentUtils.getPaymentTotal(allOrders, allDiscounts, decimals)
      : 0

  orders.forEach((order, index) => {
    const deal = getLocalizedDealById(order.deals.base?.dealId, dealLang, deals)
    const settings = getDealSettings(deal)
    const installments =
      calculateTotal(order.result, settings?.installments) ?? 0
    totals.installments += installments ?? 0
    if (hasResultFinalPrices(order.result)) {
      totals.hasFinalPrices = true
    }
    // First order if it has installments, we use the installments count
    if (index === 0 && settings?.installments) {
      totals.installmentCount = settings.installments
    }
    // Reset installment count if it's different from the first order
    if (settings?.installments !== totals.installmentCount) {
      totals.installmentCount = 0
    }
  })

  return {
    remote: resolvedTotal,
    local: resolvedTotal,
    display: totals.installments,
    resolved: resolvedTotal,
    installmentCount: totals.installmentCount,
    hasFinalPrices: totals.hasFinalPrices,
    includesDiscounts: true,
    showMonthly: false,
  } satisfies OrderTotal
}

/**
 * Prices don't include user applied discounts ie (codes, credits)
 * @returns OrderTotal
 */
const useOrderTotal = () => {
  const result = useOrderState((s) => s.result)
  const deals = useSession((s) => s.deals)
  const order = useOrderState((s) => s.order)
  return useOrderResultTotal({
    result,
    order,
    deals: resolveDealsContext(deals),
  })
}

export const useSavedOrderTotal = (orderId?: string) => {
  const saved = useSavedOrder(orderId)

  const result = useOrderState((s) => s.result)
  const deals = useSession((s) => s.deals)
  const order = useOrderState((s) => s.order)

  const options = saved
    ? {
        result: saved.result,
        order: saved.order,
        deals: saved.deals,
      }
    : {
        result,
        order,
        deals: resolveDealsContext(deals),
      }

  const orderTotal = useOrderResultTotal(options)
  return {
    orderTotal,
    ...options,
  }
}

export default useOrderTotal
